import { apiClient } from '@/lib/api/client';
import type {
  MetricsSummaryResponse,
  AggregatedMetrics,
  MetricsFilters,
  ProductMetric,
} from '../types/metrics.types';

interface ApiData<T> {
  data: T;
  message?: string;
}

export const metricsService = {
  async getMetricsSummary(filters?: MetricsFilters): Promise<MetricsSummaryResponse | null> {
    const params = buildParams(filters);
    const response = await apiClient.get<ApiData<MetricsSummaryResponse>>(
      `/metrics/summary?${params}`
    );
    return response.data?.data ?? null;
  },

  // Aggregate product metrics for display
  aggregateProductMetrics(productMetrics: ProductMetric[]): AggregatedMetrics {
    // Get unique products
    const productMap = new Map<
      string,
      { totalViews: number; totalSearches: number; positions: number[] }
    >();

    // Aggregate search terms
    const searchTermMap = new Map<string, number>();

    // Aggregate countries
    const countryMap = new Map<string, number>();

    let totalViews = 0;
    let totalSearchAppearances = 0;

    for (const metric of productMetrics) {
      totalViews += metric.views;
      totalSearchAppearances += metric.searchAppearances;

      // Aggregate by product
      const existing = productMap.get(metric.productId) || {
        totalViews: 0,
        totalSearches: 0,
        positions: [],
      };
      existing.totalViews += metric.views;
      existing.totalSearches += metric.searchAppearances;
      productMap.set(metric.productId, existing);

      // Aggregate search terms
      for (const search of metric.searchHistory) {
        const term = search.searchTerm.toLowerCase();
        searchTermMap.set(term, (searchTermMap.get(term) || 0) + 1);

        // Add position for average calculation
        existing.positions.push(search.resultPosition);
      }

      // Aggregate countries
      for (const [country, views] of Object.entries(metric.countriesData)) {
        countryMap.set(country, (countryMap.get(country) || 0) + views);
      }
    }

    // Convert to arrays and sort
    const topSearchTerms = Array.from(searchTermMap.entries())
      .map(([term, count]) => ({ term, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const topCountries = Array.from(countryMap.entries())
      .map(([country, views]) => ({ country, views }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    const productPerformance = Array.from(productMap.entries())
      .map(([productId, data]) => ({
        productId,
        totalViews: data.totalViews,
        totalSearches: data.totalSearches,
        avgPosition:
          data.positions.length > 0
            ? data.positions.reduce((a, b) => a + b, 0) / data.positions.length
            : 0,
      }))
      .sort((a, b) => b.totalViews - a.totalViews);

    return {
      totalViews,
      totalSearchAppearances,
      totalProducts: productMap.size,
      topSearchTerms,
      topCountries,
      productPerformance,
    };
  },
};

function buildParams(filters?: MetricsFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.period) params.set('period', filters.period);
  if (filters?.timeRange) params.set('timeRange', filters.timeRange);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.shopId) params.set('shopId', filters.shopId);
  return params;
}
